'use client'
import React, { FC, PropsWithChildren, createContext, useEffect, useState } from 'react'
import axios from 'axios'
import Wallet from '@/utils/wallet'
import { API_URL, MEDIA_URL } from '@/utils/constants'
import useDebouncedCallback from '@/utils/hooks/useDebouncedCallback'
import { HashMatch } from '@/utils/types/hash-match'
import { InscribeContentType, INSCRIBE_SUCCESSFUL } from '@/utils/types/inscribe'
import event from '@/utils/event'
export const UploadContext = createContext<any>(null)
import { NETWORK_CONFIG } from '@/utils/constants'

function getQueryVariable(variable: any) {
	if (typeof window === 'undefined') {
		return
	}

	var query = window.location.search.substring(1)
	var vars = query.split('&')
	for (var i = 0; i < vars?.length; i++) {
		var pair = vars[i].split('=')
		if (decodeURIComponent(pair[0]) == variable) {
			return decodeURIComponent(pair[1])
		}
	}
}

async function _downloadFile(_id: any) {}

const POSTAGE = (NETWORK_CONFIG as any).postage

const UploadManager: FC<
	PropsWithChildren<{
		estimateUrl?: string
		hashMatch?: HashMatch
		file?: File
		contentType?: InscribeContentType
		files?: File[]
	}>
> = (props) => {
	const [step, setStep] = useState(INSCRIBE_SUCCESSFUL)
	const [files, setFiles] = useState(
		props?.file ? [{ file: props.file }] : props.files ? props.files.map((e) => ({ file: e })) : []
	)
	const [fileSize, setFileSize] = useState(0)
	const [cost, setCost] = React.useState(0)
	const [feeRate, setFeeRate] = React.useState(10.0)
	const [minFeeRate, setMinFeeRate] = React.useState(5)
	const [estimateData, setEstimate] = React.useState<any>(null)
	const [uploadComplete, setUploadComplete] = useState(false)
	const [inscriptionId, setInscriptionId] = React.useState('')
	const [postage, setPostage] = React.useState(POSTAGE)
	const [contentType, setContentType] = React.useState<InscribeContentType>(props?.contentType)

	const hash = props?.hashMatch?.hash || getQueryVariable('hash')

	React.useEffect(() => {
		if (hash) {
			// Was a Vercel function proxy (`/api/media/<hash>` -> media.ordinalswallet.com)
			// — `media.ordinalswallet.com` already serves `access-control-allow-origin:
			// https://ordinalswallet.com`, so we can fetch it from the browser
			// directly and skip the function invocation.
			fetch(`${MEDIA_URL}/${hash}.png`)
				.then((res) => res.arrayBuffer())
				.then((res) => {
					const blob = new Blob([res])
					let file = new File([blob], `${hash}.png`, { type: 'image/png' })
					setFiles([{ file }])
				})
		}
	}, [hash])

	const updateFiles = (files: any[]) => {
		if (!files?.length) {
			setFiles([])
			return
		}

		const res = files.filter((e) => {
			if (!e?.file) return false
			if (e.file.size > 300000) {
				event.emit('snackbar', {
					variant: 'error',
					title: `${e.file.name} exceeds file size limit`
				})
			}
			return e.file.size <= 300000
		})

		if (res?.length > 0) {
			setFiles(res)
		}
	}

	const runEstimate = React.useCallback(async () => {
		let file = files[0]
		if (!file?.file || !feeRate) {
			return
		}

		const { data } = await axios.post(props.estimateUrl || `${API_URL}/inscribe/estimate`, {
			destination: Wallet.getTaprootAddress(),
			fee_rate: feeRate,
			postage: postage,
			file_sizes: files.flatMap((e) => (e?.file ? [e.file.size] : [])),
			file_hash: hash,
			content_type: file.file.type
		})
		setEstimate(data)
		setCost(data.total_cost)
	}, [feeRate, files, hash, postage, props.estimateUrl])

	const estimate = useDebouncedCallback(runEstimate, 100)

	React.useEffect(() => {
		let file = files[0]
		if (!file?.file) {
			return
		}

		requestAnimationFrame(() => {
			setEstimate(null)
		})
		estimate()
	}, [estimate, feeRate, files])

	useEffect(() => {
		if (files?.length === 0) {
			requestAnimationFrame(() => {
				setUploadComplete(false)
			})
		}
	}, [files])

	return (
		<UploadContext.Provider
			value={{
				estimateData,
				step,
				setStep,
				files,
				setFiles: updateFiles,
				fileSize,
				setFileSize,
				cost,
				setCost,
				uploadComplete,
				setUploadComplete,
				inscriptionId,
				setInscriptionId,
				feeRate,
				setFeeRate,
				postage,
				setPostage,
				minFeeRate,
				setMinFeeRate,
				contentType,
				setContentType
			}}
		>
			{props.children}
		</UploadContext.Provider>
	)
}

export default UploadManager
